import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons'
import { Button, Flex, Form, Input, Table } from 'antd'
import { TableColumnsType } from 'antd/lib'
import { useState } from 'react'

import { EditableFormItem } from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'

import { RecordType, tableData } from '../data'

const formItemProps = {
  rules: [{ required: true }],
  style: { fontWeight: 400 }
}

export const AnnuallyReportTable = () => {
  const [form] = Form.useForm()
  const [editingKey, setEditingKey] = useState<number>()
  const pagination = useTablePagination()
  const { t } = useLocales()

  const isEditing = (record: RecordType) => record.id === editingKey

  const onEdit = (record: RecordType) => {
    setEditingKey(record.id)
  }

  const onCancel = (record: RecordType, index: number) => {
    form.setFieldsValue({ [index]: record })
    setEditingKey(undefined)
  }

  const onSave = async () => {
    await form.validateFields()
    setEditingKey(undefined)
  }

  const columns: TableColumnsType<RecordType> = [
    {
      key: 'code',
      title: t('organizational_structure.cuatm_code'),
      render: (_, record, index) => (
        <EditableFormItem
          isEditing={isEditing(record)}
          name={[index, 'code']}
          {...formItemProps}
        >
          <Input />
        </EditableFormItem>
      )
    },
    {
      key: 'region',
      title: t('organizational_structure.region'),
      render: (_, record, index) => (
        <EditableFormItem
          isEditing={isEditing(record)}
          name={[index, 'region']}
          {...formItemProps}
        >
          <Input />
        </EditableFormItem>
      )
    },
    {
      key: 'people count',
      title: t('reports_administration.people_count'),
      render: (_, record, index) => (
        <EditableFormItem
          isEditing={isEditing(record)}
          name={[index, 'people_count']}
          {...formItemProps}
        >
          <Input />
        </EditableFormItem>
      )
    },
    {
      key: 'percentage',
      title: t('reports_administration.total_percentage'),
      render: (_, record, index) => (
        <EditableFormItem
          isEditing={isEditing(record)}
          name={[index, 'total_percentage']}
          {...formItemProps}
        >
          <Input />
        </EditableFormItem>
      )
    },
    {
      key: 'age 0-14',
      title: t('reports_administration.age_0_14'),
      render: (_, record, index) => (
        <EditableFormItem
          isEditing={isEditing(record)}
          name={[index, 'age_0_14']}
          {...formItemProps}
        >
          <Input />
        </EditableFormItem>
      )
    },
    {
      key: 'age 15-24',
      title: t('reports_administration.age_15_24'),
      render: (_, record, index) => (
        <EditableFormItem
          isEditing={isEditing(record)}
          name={[index, 'age_15_24']}
          {...formItemProps}
        >
          <Input />
        </EditableFormItem>
      )
    },
    {
      key: 'age 25-44',
      title: t('reports_administration.age_25_44'),
      render: (_, record, index) => (
        <EditableFormItem
          isEditing={isEditing(record)}
          name={[index, 'age_25_44']}
          {...formItemProps}
        >
          <Input />
        </EditableFormItem>
      )
    },
    {
      key: 'age 45-64',
      title: t('reports_administration.age_45_64'),
      render: (_, record, index) => (
        <EditableFormItem
          isEditing={isEditing(record)}
          name={[index, 'age_45_64']}
          {...formItemProps}
        >
          <Input />
        </EditableFormItem>
      )
    },
    {
      key: 'age 65+',
      title: t('reports_administration.age_65'),
      render: (_, record, index) => (
        <EditableFormItem
          isEditing={isEditing(record)}
          name={[index, 'age_65']}
          {...formItemProps}
        >
          <Input />
        </EditableFormItem>
      )
    },
    {
      key: 'men',
      title: t('reports_administration.men'),
      render: (_, record, index) => (
        <EditableFormItem
          isEditing={isEditing(record)}
          name={[index, 'men']}
          {...formItemProps}
        >
          <Input />
        </EditableFormItem>
      )
    },
    {
      key: 'women',
      title: t('reports_administration.women'),
      render: (_, record, index) => (
        <EditableFormItem
          isEditing={isEditing(record)}
          name={[index, 'women']}
          {...formItemProps}
        >
          <Input />
        </EditableFormItem>
      )
    },
    {
      key: 'actions',
      fixed: 'right',
      render: (_, record, index) => {
        return isEditing(record) ? (
          <Flex gap={8}>
            <Button
              icon={<CloseOutlined />}
              onClick={() => onCancel(record, index)}
            />

            <Button type="primary" icon={<CheckOutlined />} onClick={onSave} />
          </Flex>
        ) : (
          <Button icon={<EditOutlined />} onClick={() => onEdit(record)} />
        )
      }
    }
  ]

  return (
    <Form form={form} requiredMark={false} initialValues={tableData}>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={tableData}
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: tableData.length }}
      />
    </Form>
  )
}
